/* eslint-disable simple-import-sort/imports */
import React from 'react';
import { styled } from 'styled-components';

import { Reveal, Stagger, StaggerItem } from 'pages/HomeV2/components/Motion';
import { Container, Eyebrow, Lead, Title, v2 } from 'pages/HomeV2/v2';

import { CheckIcon, GiftIcon } from '../icons';

const Wrapper = styled.section`
  position: relative;
  background: ${v2.bg};
  border-top: 1px solid ${v2.border};
  padding: clamp(64px, 8vw, 110px) 0;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 40px;
  align-items: center;

  @media (min-width: 900px) {
    grid-template-columns: minmax(0, 1.05fr) minmax(0, 0.95fr);
    gap: clamp(40px, 6vw, 96px);
  }
`;

const Copy = styled.div`
  display: flex;
  flex-direction: column;
  gap: 22px;

  ${Title} {
    font-size: clamp(1.8rem, 3.8vw, 2.9rem);
  }
`;

/* Playful nod to FOXI's billing view: the goodie bag as a tiny
   "Leistungsübersicht" with two positions. */
const Receipt = styled.div`
  position: relative;
  border-radius: 24px;
  border: 1px solid ${v2.borderStrong};
  background: ${v2.bgElevated};
  box-shadow:
    0 30px 70px rgba(0, 0, 0, 0.45),
    0 0 60px rgba(255, 159, 79, 0.08);
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(
      80% 60% at 100% 0%,
      rgba(255, 159, 79, 0.16),
      transparent 60%
    );
    pointer-events: none;
  }
`;

const ReceiptHead = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 18px 22px;
  border-bottom: 1px solid ${v2.border};
  font-family: ${v2.fontBody};
  font-size: 12.5px;
  font-weight: 700;
  letter-spacing: 1.6px;
  text-transform: uppercase;
  color: ${v2.textFaint};

  span.badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    border-radius: 10px;
    background: ${v2.orangeSoft};
    color: ${v2.orange};

    svg {
      width: 18px;
      height: 18px;
    }
  }
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 34px minmax(0, 1fr) auto;
  align-items: center;
  gap: 14px;
  padding: 18px 22px;
  border-bottom: 1px solid ${v2.border};

  .num {
    font-family: ${v2.fontBody};
    font-size: 13px;
    font-weight: 700;
    color: ${v2.textFaint};
    letter-spacing: 1px;
  }

  .label {
    font-family: ${v2.fontDisplay};
    font-size: clamp(1rem, 1.3vw, 1.12rem);
    font-weight: 700;
    letter-spacing: -0.01em;
    color: ${v2.text};
    line-height: 1.3;

    small {
      display: block;
      margin-top: 3px;
      font-family: ${v2.fontBody};
      font-size: 13.5px;
      font-weight: 400;
      color: ${v2.textMuted};
    }
  }

  @media (max-width: 420px) {
    grid-template-columns: minmax(0, 1fr) auto;

    .num {
      display: none;
    }
  }
`;

const Status = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 12px;
  border-radius: 100px;
  font-family: ${v2.fontBody};
  font-size: 12.5px;
  font-weight: 700;
  white-space: nowrap;
  border: 1px solid
    ${({ $tone }) =>
      $tone === 'green'
        ? 'rgba(52, 211, 153, 0.4)'
        : 'rgba(255, 159, 79, 0.45)'};
  background: ${({ $tone }) =>
    $tone === 'green' ? 'rgba(52, 211, 153, 0.12)' : v2.orangeSoft};
  color: ${({ $tone }) => ($tone === 'green' ? v2.green : v2.orange)};

  svg {
    width: 14px;
    height: 14px;
  }
`;

const Sum = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 22px;
  font-family: ${v2.fontBody};
  font-size: 14px;
  color: ${v2.textMuted};

  b {
    font-family: ${v2.fontDisplay};
    font-size: 15px;
    font-weight: 700;
    color: ${v2.text};
    text-align: right;
  }
`;

const GoodieMoment = () => (
  <Wrapper aria-labelledby="goodie-moment-title">
    <Container>
      <Grid>
        <Copy>
          <Reveal>
            <Eyebrow $accent="orange">Goodie-Moment</Eyebrow>
          </Reveal>
          <Reveal delay={0.1}>
            <Title id="goodie-moment-title">
              Erst die Schokolade. Dann die Software.{' '}
              <span aria-hidden>🍫</span>
            </Title>
          </Reveal>
          <Reveal delay={0.2}>
            <Lead>
              Wir wissen, wie voll der Alltag in einer Zahnarztpraxis sein kann.
              FOXI wurde entwickelt, um administrative Abläufe einfacher,
              schneller und digitaler zu machen.
            </Lead>
          </Reveal>
        </Copy>

        <Stagger delay={0.15} gap={0.1}>
          <StaggerItem>
            <Receipt aria-hidden>
              <ReceiptHead>
                <span className="badge">
                  <GiftIcon />
                </span>
                Übersicht · Goodie-Bag
              </ReceiptHead>
              <Row>
                <span className="num">01</span>
                <span className="label">
                  Nervennahrung fürs Team
                  <small>Pralinen &amp; Kaugummis – direkt aus der Tüte</small>
                </span>
                <Status $tone="green">
                  <CheckIcon />
                  erledigt
                </Status>
              </Row>
              <Row>
                <span className="num">02</span>
                <span className="label">
                  Weniger Verwaltungsstress
                  <small>
                    Dokumentation, Abrechnung, Anamnese, Termine, Recall
                  </small>
                </span>
                <Status $tone="orange">mit FOXI</Status>
              </Row>
              <Sum>
                <span>Summe</span>
                <b>Ein entspannteres Praxisteam</b>
              </Sum>
            </Receipt>
          </StaggerItem>
        </Stagger>
      </Grid>
    </Container>
  </Wrapper>
);

export default GoodieMoment;
